import { getLabelStyles } from '@sharedCR/common/form/Label';
import { getAddressFieldsConfig } from 'app/pages/loanApplication/LoanApplication.sharedConfigs';
import { MarkedAppFormsTreeFieldId } from 'app/services/autoLockForms/autoLockForms.fieldIds';
import dayjs from 'dayjs';
import { compose } from 'redux';

import { COMMON_DATE_FORMAT } from 'constants/date';
import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import {
  baseStatusOptions,
  boolBaseStatusOptions,
  maritalStatusOptions,
  suffixOptions,
} from 'constants/options';
import { ItemGroupAlignment } from 'constants/ui';
import { getConditionalArray } from 'utils/data';
import { fillConfigFieldsWithKeys } from 'utils/loanManagement';

import { BorrowersTestIds } from '../../../../../Borrowers.testIds';
import { contactsSectionStyles, phoneStyles } from '../../Borrower/PersonalInfo/PersonaInfo.styles';

const MIN_BORROWER_AGE = 18;

const currentDate = Date.now();

const getPersonalInfoConfigFields = ({ entityId }) => ({
  poaName: {
    fieldId: 'powerOfAttorneyName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].powerOfAttorney.name`,
    inputProps: {
      placeholder: '-',
      label: 'POA Name',
      disabled: false,
      required: false,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.NBE_PERSONAL_INFO_FORM_POA_NAME_INPUT,
      'data-test-value': 'poa-name',
    },
  },
  poaVerbiage: {
    fieldId: 'powerOfAttorneyVerbiage',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].powerOfAttorney.verbiage`,
    inputProps: {
      placeholder: '-',
      label: 'POA Verbiage',
      disabled: false,
      required: false,
      multiline: true,
      rows: 8,
      maxLength: 1000,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.NBE_PERSONAL_INFO_FORM_POA_VERBIAGE_INPUT,
      'data-test-value': 'poa-verbiage',
    },
  },
  firstName: {
    fieldId: 'firstName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].firstName`,
    inputProps: {
      placeholder: '-',
      label: 'First Name',
      disabled: false,
      required: true,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.NBE_PERSONAL_INFO_FORM_FIRST_NAME_INPUT,
      'data-test-value': 'first-name',
    },
  },
  middleName: {
    fieldId: 'middleName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].middleName`,
    inputProps: {
      placeholder: '-',
      label: 'Middle Name',
      disabled: false,
      required: false,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.NBE_PERSONAL_INFO_FORM_MIDDLE_NAME_INPUT,
      'data-test-value': 'middle-name',
    },
  },
  lastName: {
    fieldId: 'lastName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].lastName`,
    inputProps: {
      placeholder: '-',
      label: 'Last Name',
      disabled: false,
      required: true,
      variant: 'outlined',
      dataTestId: BorrowersTestIds.NBE_PERSONAL_INFO_FORM_LAST_NAME_INPUT,
      'data-test-value': 'last-name',
    },
  },
  suffix: {
    fieldId: 'suffix',
    type: FieldType.SELECT,
    nameInModel: `[${entityId}].suffix`,
    inputProps: {
      label: 'Suffix',
      placeholder: '-',
      disabled: false,
      required: false,
      variant: 'outlined',
      options: suffixOptions,
      dataTestId: BorrowersTestIds.NBE_PERSONAL_INFO_FORM_SUFFIX_SELECT,
      'data-test-value': suffixOptions[0].value,
    },
  },
  email: {
    fieldId: 'email',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].email`,
    inputProps: {
      columns: 3,
      type: FieldInputType.EMAIL,
      placeholder: '-',
      label: 'Email Address',
      required: true,
      variant: 'outlined',
    },
  },
  cellPhoneNumber: {
    fieldId: 'cellPhoneNumber',
    type: FieldType.TEL,
    nameInModel: `[${entityId}].cellPhone`,
    inputProps: {
      'data-field-id': MarkedAppFormsTreeFieldId.NBE_CELL_PHONE_NUMBER,
      autoComplete: 'tel-extension',
      customStyles: phoneStyles,
      columns: 3,
      label: 'Cell',
      placeholder: '-',
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEL_EXT,
      dataTestId: BorrowersTestIds.NBE_PERSONAL_INFO_FORM_CELL_NUMBER_INPUT,
      'data-test-value': '731-222-2222',
    },
  },
  ssn: {
    fieldId: 'ssn',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].ssn`,
    inputProps: {
      autoComplete: 'off',
      placeholder: '-',
      label: 'Social Security Number',
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.SSN,
      dataTestId: BorrowersTestIds.NBE_PERSONAL_INFO_FORM_SOCIAL_SECURITY_NUMBER_INPUT,
      'data-test-value': '123222222',
      'data-test-formatted-value': '123-22-2222',
    },
  },
  dateOfBirth: {
    fieldId: 'dateOfBirth',
    type: FieldType.DATE,
    nameInModel: `[${entityId}].dateOfBirth`,
    inputProps: {
      label: 'Date of Birth',
      disabled: false,
      required: false,
      variant: 'outlined',
      format: COMMON_DATE_FORMAT,
      type: FieldInputType.DATE,
      maxDate: dayjs(currentDate).subtract(MIN_BORROWER_AGE, 'years').format(COMMON_DATE_FORMAT),
      maxDateValidationMessage: 'Age should be more than 18 years old',
    },
  },
  relation: {
    fieldId: 'relation',
    type: FieldType.SELECT,
    nameInModel: `[${entityId}].relation`,
    inputProps: {
      label: 'Spouse of',
      placeholder: '-',
      columns: 2,
      disabled: false,
      required: true,
      options: [],
      dataTestId: BorrowersTestIds.NBE_PERSONAL_INFO_FORM_RELATION_SELECT,
    },
  },
  maritalStatus: {
    fieldId: 'maritalStatus',
    type: FieldType.SELECT,
    nameInModel: `[${entityId}].maritalStatus`,
    inputProps: {
      placeholder: '-',
      label: 'Marital status',
      disabled: false,
      required: true,
      options: maritalStatusOptions,
      dataTestId: BorrowersTestIds.NBE_PERSONAL_INFO_FORM_MARITAL_STATUS_SELECT,
    },
  },
  ownershipInterest: {
    fieldId: 'ownershipInterest',
    type: FieldType.RADIO,
    nameInModel: `[${entityId}].ownershipInterest`,
    inputProps: {
      label: 'Ownership Interest:',
      disabled: false,
      required: false,
      options: baseStatusOptions,
      columns: 4,
      align: ItemGroupAlignment.HORIZONTAL,
    },
  },
  isTrusteeOfTrust: {
    fieldId: 'isTrusteeOfTrust',
    type: FieldType.CHECKBOX,
    nameInModel: `[${entityId}].isTrusteeOfTrust`,
    inputProps: {
      label: 'Is Trustee of Trust',
      required: false,
      variant: 'outlined',
    },
  },
  fhaEligible: {
    fieldId: 'fhaEligible',
    type: FieldType.SELECT,
    nameInModel: `[${entityId}].fhaEligible`,
    inputProps: {
      placeholder: '-',
      label: 'FHA Eligible',
      disabled: false,
      required: false,
      options: boolBaseStatusOptions,
      variant: 'outlined',
    },
  },
  spouseFirstName: {
    fieldId: 'spouseFirstName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].spouseFirstName`,
    inputProps: {
      placeholder: '-',
      label: 'Spouse First Name',
      disabled: false,
      required: true,
    },
  },
  spouseLastName: {
    fieldId: 'spouseLastName',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].spouseLastName`,
    inputProps: {
      placeholder: '-',
      label: 'Spouse Last Name',
      disabled: false,
      required: true,
    },
  },
  spouseEmail: {
    fieldId: 'spouseEmail',
    type: FieldType.INPUT,
    nameInModel: `[${entityId}].spouseEmail`,
    inputProps: {
      type: FieldInputType.EMAIL,
      placeholder: '-',
      label: 'Spouse Email',
      required: true,
    },
  },
});

export const getPersonalInfoConfig = compose(
  fillConfigFieldsWithKeys,
  ({
    entityId,
    section,
    filteredCounties = [],
    isCurrentSpouseOther,
    isSpouseSignatureContext,
    isMarried,
    isFha = false,
    relationOptions = [],
    onRelationInputChange,
    onMaritalStatusInputChange,
    isConfigForSpouseEntity = false,
  }) => {
    const personalInfoConfigFields = getPersonalInfoConfigFields({ entityId });

    const generalGroup = {
      type: FieldType.GROUP,
      fields: [
        personalInfoConfigFields.firstName,
        personalInfoConfigFields.middleName,
        personalInfoConfigFields.lastName,
        personalInfoConfigFields.suffix,
        personalInfoConfigFields.ssn,
        personalInfoConfigFields.dateOfBirth,
        {
          ...personalInfoConfigFields.maritalStatus,
          inputProps: {
            ...personalInfoConfigFields.maritalStatus.inputProps,
            required: isSpouseSignatureContext,
            customOnChange: onMaritalStatusInputChange,
          },
        },
        ...getConditionalArray(isMarried, () => [
          {
            ...personalInfoConfigFields.relation,
            inputProps: {
              ...personalInfoConfigFields.relation.inputProps,
              required: isSpouseSignatureContext,
              options: relationOptions,
              customOnChange: onRelationInputChange,
            },
          },
        ]),
      ],
      key: `${entityId}-${section}-general`,
    };

    const spouseFieldsGroup = {
      type: FieldType.GROUP,
      fields: [
        ...getConditionalArray(isCurrentSpouseOther, () =>
          [
            personalInfoConfigFields.spouseFirstName,
            personalInfoConfigFields.spouseLastName,
            personalInfoConfigFields.spouseEmail,
          ].map(field => ({
            ...field,
            inputProps: { ...field.inputProps, required: isSpouseSignatureContext },
          })),
        ),
        ...getConditionalArray(isSpouseSignatureContext, [
          personalInfoConfigFields.ownershipInterest,
        ]),
      ],
      key: `${entityId}-${section}-spouseInfo`,
    };

    if (isConfigForSpouseEntity) {
      return { groups: [generalGroup, spouseFieldsGroup] };
    }

    return {
      groups: [
        generalGroup,
        ...getConditionalArray(isMarried && spouseFieldsGroup.fields.length > 0, [
          spouseFieldsGroup,
        ]),
        {
          type: FieldType.GROUP,
          fields: Object.values(
            getAddressFieldsConfig({
              isAllFieldsNonRequired: true,
              prefix: `[${entityId}].address`,
              filteredCounties,
            }),
          ),
          key: `${entityId}-${section}-address`,
          lockAsGroup: true,
        },
        {
          title: { label: 'Contact Information', titleStyles: getLabelStyles() },
          type: FieldType.GROUP,
          fields: [personalInfoConfigFields.cellPhoneNumber],
          key: `${entityId}-${section}-contact`,
          customStyles: contactsSectionStyles,
        },
        {
          type: FieldType.GROUP,
          fields: [personalInfoConfigFields.email],
          key: `${entityId}-${section}-email`,
          customStyles: contactsSectionStyles,
        },
        {
          type: FieldType.GROUP,
          fields: [
            personalInfoConfigFields.isTrusteeOfTrust,
            ...getConditionalArray(isFha, [personalInfoConfigFields.fhaEligible]),
          ],
          key: `${entityId}-${section}-trustee`,
        },
        {
          title: { label: 'Power of Attorney', titleStyles: getLabelStyles() },
          type: FieldType.GROUP,
          fields: [personalInfoConfigFields.poaName, personalInfoConfigFields.poaVerbiage],
          key: `${entityId}-${section}-poa`,
        },
      ],
    };
  },
);
